import { createSlice, createSelector } from '@reduxjs/toolkit';
import {
  SubscriptionState,
  SubscriptionStatus,
} from '../../interfaces/subscription';
import { getSubscriptionStatus } from '../ActionTypes/subscriptionActionTypes';
import { setSubscriptionInfo } from '../../services/subscription';
/*
import {
  setSubscriptionInfo,
  getLicense,
  isTeamUser,
} from '../../services/subscription';
*/
import { Notify } from '../../services/notificationService';
import { RootState } from '../store';
import {
  RESTRICTED_SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_LABELS,
} from '../../constants/subscription';
import { getSubscriptionStatusMessage } from '../../constants/texts';
/*
import {
  NO_VALID_ORG_LICENSE,
  getSubscriptionStatusMessage,
  GUEST_LICENSE,
} from '../../constants/texts';
import { OrganizationLicense } from '../../enums/account';
*/

const initialState: SubscriptionState = {
  status: SubscriptionStatus.UNINITIALIZED,
  subscriptionInfo: undefined,
  loading: false,
  error: null,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    resetSubscription: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubscriptionStatus.fulfilled, (state, action) => {
        // Handle the actual API response structure
        const responseData = action.payload.data;
        const subscriptionData = responseData?.subscription;

        // Convert string status to enum value - default to UNKNOWN if subscription data is missing or status is undefined
        const rawStatus = subscriptionData?.status || responseData.status;
        const status = rawStatus
          ? (rawStatus as SubscriptionStatus)
          : SubscriptionStatus.UNKNOWN;
        state.status = status;

        const stripeCustomerId =
          subscriptionData?.stripeCustomerId ||
          responseData.stripeCustomerId ||
          'unknown';

        // Create a subscription data object that matches the expected interface
        const subscriptionDataObj = {
          ...(subscriptionData || {}), // Use empty object if subscriptionData is undefined
          status: status, // Override with computed status (using the properly typed enum value)
          message: responseData.message,
        };

        // Store the full subscription info with organization context for permission checks
        state.subscriptionInfo = {
          ...responseData,
          subscription: subscriptionDataObj,
          stripeCustomerId,
        };

        state.loading = false;

        // Transform API response to ensure consistent camelCase for downstream services
        // This prevents data casing inconsistencies that could affect TokenService functions
        if (state.subscriptionInfo) {
          setSubscriptionInfo(state.subscriptionInfo);
        }
      })
      .addCase(getSubscriptionStatus.rejected, (state, action) => {
        // Store only serializable error data
        const errorData = action.payload as Record<string, unknown>;
        state.error = errorData;
        state.loading = false;

        // Display user-friendly error message
        const errorMessage =
          errorData.errorCode === 'NETWORK_ERROR'
            ? 'Unable to connect to server. Please check your connection.'
            : (errorData.message as string) ||
              'Failed to fetch subscription status';

        Notify.error(errorMessage);
      });
  },
});

// TODO Use these selectors consistently through out the app
// Selector to compute readonly state
export const selectSubscriptionReadonlyState = createSelector(
  [
    (state: RootState) => state.subscription?.status,
    (state: RootState) => state.subscription?.subscriptionInfo?.subscription,
  ],
  (status, subscriptionData) => {
    let isReadonly = true;
    let readonlyTitle = '';

    const restrictedSubscription =
      !status ||
      RESTRICTED_SUBSCRIPTION_STATUSES.includes(
        status?.toLowerCase() as SubscriptionStatus
      );

    if (restrictedSubscription) {
      readonlyTitle = getSubscriptionStatusMessage(
        status as SubscriptionStatus
      );
    } else {
      /*
      // Check if user has org license
      if (isTeamUser()) {
        // Use the existing service logic since license info is handled separately from subscription data
        const orgLicense = getLicense();

        if (
          !orgLicense &&
          status !== SubscriptionStatus.FREE &&
          !subscriptionData?.freePlan
        ) {
          readonlyTitle = NO_VALID_ORG_LICENSE;
        } else if (orgLicense === OrganizationLicense.Guest) {
          readonlyTitle = GUEST_LICENSE;
        } else {
          isReadonly = false;
        }
      } else {
        isReadonly = false;
      }
      */
      isReadonly = false;
    }

    return { isReadonly, readonlyTitle };
  }
);

// Simple boolean selector for components that only need the readonly state
export const selectIsSubscriptionReadonly = createSelector(
  [selectSubscriptionReadonlyState],
  (readonlyState) => readonlyState.isReadonly
);

// Selector for subscription status
export const selectSubscriptionStatus = createSelector(
  [(state: RootState) => state.subscription.status],
  (status) => status
);

// Selector for subscription status label
export const selectSubscriptionStatusLabel = createSelector(
  [(state: RootState) => state.subscription.status],
  (status) => {
    return (
      SUBSCRIPTION_LABELS[status as keyof typeof SUBSCRIPTION_LABELS] ??
      SUBSCRIPTION_LABELS[SubscriptionStatus.UNKNOWN]
    );
  }
);

export const selectSubscriptionData = createSelector(
  [(state: RootState) => state.subscription],
  (subscription) => ({
    status: subscription.status,
    isFreePlan: subscription.subscriptionInfo?.subscription?.freePlan ?? false,
    loading: subscription.loading,
    hasValidSubscription: subscription.status === SubscriptionStatus.ACTIVE,
  })
);

export const { resetSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
